import { useState } from 'react';
import { Link } from 'react-router';
import {
  Bell,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCheck,
  Bot,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAlerts, useAcknowledgeAlert, useAcknowledgeAllAlerts, type AlertsParams } from '@/hooks/use-alerts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { timeAgo } from '@/lib/format';
import type { Alert, AlertSeverity } from '@/types/api';

const ALERTS_PER_PAGE = 30;

export default function AlertsPage() {
  const [severity, setSeverity] = useState<AlertSeverity | undefined>(undefined);
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [page, setPage] = useState(1);

  const params: AlertsParams = {
    severity,
    includeAcknowledged: showAcknowledged,
    page,
    limit: ALERTS_PER_PAGE,
  };

  const { data, isLoading, error } = useAlerts(params, { refetchInterval: 15_000 });
  const acknowledge = useAcknowledgeAlert();
  const acknowledgeAll = useAcknowledgeAllAlerts();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Failed to load alerts</p>
        <p className="text-xs text-ink-3">Check that the API server is running.</p>
      </div>
    );
  }

  const alerts = data?.alerts ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const unreadOnPage = alerts.filter((a) => !a.acknowledged).length;

  // Group by date
  const grouped = groupByDate(alerts);

  const handleMarkAllRead = () => {
    acknowledgeAll.mutate();
  };

  // Reset page to 1 when filters change
  const handleSeverityChange = (s: AlertSeverity | undefined) => {
    setSeverity(s);
    setPage(1);
  };

  const handleToggleAcknowledged = () => {
    setShowAcknowledged(!showAcknowledged);
    setPage(1);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">Alerts</h1>
          <p className="text-xs text-ink-3 mt-0.5">
            {total} alert{total !== 1 ? 's' : ''}
            {!showAcknowledged && ` unread`}
          </p>
        </div>
        {unreadOnPage > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAllRead}
            disabled={acknowledgeAll.isPending}
          >
            <CheckCheck size={14} />
            Mark All Read
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-1">
          {([undefined, 'CRITICAL', 'WARNING', 'INFO'] as const).map((s) => (
            <button
              key={s ?? 'all'}
              onClick={() => handleSeverityChange(s)}
              className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                severity === s
                  ? 'bg-orange-bg text-orange border border-orange/20'
                  : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2'
              }`}
            >
              {s ?? 'All'}
            </button>
          ))}
        </div>
        <button
          onClick={handleToggleAcknowledged}
          className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-all cursor-pointer ${
            showAcknowledged
              ? 'bg-orange-bg text-orange border border-orange/20'
              : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2'
          }`}
        >
          Show Read
        </button>
      </div>

      {/* Alert List */}
      {alerts.length === 0 ? (
        <div className="glass-sm rounded-lg p-10 text-center">
          <Bell size={40} className="text-orange/40 mx-auto mb-3" />
          <h3 className="text-sm font-bold mb-1">No alerts</h3>
          <p className="text-xs text-ink-3">
            {severity ? 'No alerts match this filter.' : 'Everything looks good!'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ label, items }) => (
            <div key={label}>
              <h3 className="text-[10px] font-bold text-ink-4 uppercase tracking-wider mb-2">
                {label}
              </h3>
              <div className="space-y-1.5">
                {items.map((alert) => (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    onAcknowledge={() => acknowledge.mutate(alert.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-[10px] text-ink-4">
            Page {page} of {totalPages} ({total} total)
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page <= 1}
              className="px-2 py-1 rounded-md bg-surface border border-glass-border text-[10px] font-bold text-ink-3 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              First
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded-md bg-surface border border-glass-border text-ink-3 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronLeft size={14} />
            </button>
            {getPageNumbers(page, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="px-1 text-[10px] text-ink-4">...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p as number)}
                  className={`min-w-[28px] py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all ${
                    page === p
                      ? 'bg-orange-bg text-orange border border-orange/20'
                      : 'bg-surface border border-glass-border text-ink-3 hover:bg-surface-2'
                  }`}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1.5 rounded-md bg-surface border border-glass-border text-ink-3 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <ChevronRight size={14} />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="px-2 py-1 rounded-md bg-surface border border-glass-border text-[10px] font-bold text-ink-3 hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────

function AlertRow({
  alert,
  onAcknowledge,
}: {
  alert: Alert;
  onAcknowledge: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const severityIcon = {
    CRITICAL: <AlertCircle size={16} className="text-red" />,
    WARNING: <AlertTriangle size={16} className="text-amber" />,
    INFO: <Info size={16} className="text-blue" />,
  };

  const severityColor = {
    CRITICAL: 'red' as const,
    WARNING: 'amber' as const,
    INFO: 'blue' as const,
  };

  return (
    <div
      className={`glass-sm rounded-lg p-3 transition-all cursor-pointer ${
        alert.acknowledged ? 'opacity-50' : ''
      }`}
      onClick={() => {
        setExpanded(!expanded);
        if (!alert.acknowledged) onAcknowledge();
      }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {severityIcon[alert.severity]}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-xs font-bold ${alert.acknowledged ? 'text-ink-3' : ''}`}>
              {alert.title}
            </span>
            <Badge color={severityColor[alert.severity]}>{alert.severity}</Badge>
            <Badge color="default">{alert.category.replace(/_/g, ' ')}</Badge>
          </div>

          <p className={`text-[10px] text-ink-3 ${expanded ? '' : 'line-clamp-2'}`}>
            {alert.message}
          </p>

          {expanded && alert.botId && (
            <Link
              to={`/bots/${alert.botId}`}
              className="inline-flex items-center gap-1 text-[10px] text-orange hover:underline mt-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Bot size={10} />
              View Bot
            </Link>
          )}
        </div>

        <span className="text-[10px] text-ink-4 flex-shrink-0">
          {timeAgo(alert.createdAt)}
        </span>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────

/**
 * Generate page numbers with ellipsis for large page counts.
 * Always shows first, last, and pages around current.
 * e.g. [1, '...', 4, 5, 6, '...', 12]
 */
function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | '...')[] = [];
  const around = new Set<number>();

  // Always include first, last, and neighbors of current
  around.add(1);
  around.add(total);
  for (let i = current - 1; i <= current + 1; i++) {
    if (i >= 1 && i <= total) around.add(i);
  }

  const sorted = Array.from(around).sort((a, b) => a - b);

  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i]! - sorted[i - 1]! > 1) {
      pages.push('...');
    }
    pages.push(sorted[i]!);
  }

  return pages;
}

function groupByDate(alerts: Alert[]): { label: string; items: Alert[] }[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);

  const groups: Record<string, Alert[]> = {};

  for (const alert of alerts) {
    const date = new Date(alert.createdAt);
    let label: string;

    if (date >= today) {
      label = 'Today';
    } else if (date >= yesterday) {
      label = 'Yesterday';
    } else {
      label = 'Older';
    }

    if (!groups[label]) groups[label] = [];
    groups[label]!.push(alert);
  }

  const order = ['Today', 'Yesterday', 'Older'];
  return order
    .filter((label) => groups[label] != null)
    .map((label) => ({ label, items: groups[label]! }));
}
